import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CheckCircleIcon } from '../../../assets/icons/check-circle.svg';
import { ReactComponent as CloseCircleIcon } from '../../../assets/icons/close-circle.svg';
import { useAppBranding } from '../../../config/branding';
import { ChargingStationInterFace } from '../../../interface/charging-station.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import {
  fetchAllUnapprovedStation,
  fetchPendingNotification,
} from '../../../redux/slices/UnapprovedStationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomFileInput from '../../../shared-components/custom-file-input/CustomFileInput';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomMediaUpload from '../../../shared-components/custom-media-upload/CustomMediaUpload';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import stationApi from '../../../utils/api/charging-station';

import RejectStation from './reject-station';

interface AmenitiesDetailsProperties {
  name: string;
  key: string;
  svgIconActive: JSX.Element;
}

/**
 * View charging station details function component
 */
function ViewChargingStation({
  details,
  closeModal,
}: {
  details: ChargingStationInterFace;
  closeModal: () => void;
}) {
  const branding = useAppBranding();
  const { t } = useTranslation('station');
  const { page, limit } = useAppSelector((state) => state.unapprovedStation);
  const amenitiesDetails: AmenitiesDetailsProperties[] = [
    {
      name: t('gasStation'),
      key: 'Gas Station',
      svgIconActive: <branding.icons.GasActive />,
    },
    {
      name: t('freeWifi'),
      key: 'Free Wifi',
      svgIconActive: <branding.icons.WifiActive />,
    },
    {
      name: t('restroom'),
      key: 'Restroom',
      svgIconActive: <branding.icons.RestroomActive />,
    },
    {
      name: t('lounge'),
      key: 'Lounge',
      svgIconActive: <branding.icons.LoungeActive />,
    },
    {
      name: t('cafe'),
      key: 'Cafe',
      svgIconActive: <branding.icons.CafeActive />,
    },
    {
      name: t('shopping'),
      key: 'Shopping',
      svgIconActive: <branding.icons.ShoppingActive />,
    },
    {
      name: t('restaurant'),
      key: 'Restaurant',
      svgIconActive: <branding.icons.RestaurantActive />,
    },
    {
      name: t('relaxingArea'),
      key: 'Relaxing Area',
      svgIconActive: <branding.icons.RelaxingActive />,
    },
    {
      name: t('workShop'),
      key: 'Work Shop',
      svgIconActive: <branding.icons.SettingActive />,
    },
    {
      name: t('carWash'),
      key: 'Car Wash',
      svgIconActive: <branding.icons.CarWashActive />,
    },
  ];

  const dispatch = useAppDispatch();
  const [modalView, setModalView] = useState({
    content: <div />,
    status: false,
    notification: false,
  });

  /**
   * Close the confirm modal box function
   */
  const closeConfirmModal = () => {
    setModalView({
      content: <div />,
      status: false,
      notification: false,
    });
  };

  /**
   * Station the reject use reason
   * @param data
   */
  const stationReject = (data: ChargingStationInterFace) => {
    stationApi
      .statusChangeChargingStation(data.id, {
        submissionStatus: 'rejected',
        reason: data.reason,
      })
      .then(() => {
        closeConfirmModal();
        closeModal();
        dispatch(
          fetchAllUnapprovedStation({
            page,
            limit,
            submissionStatus: 'pending',
          }),
        );
        dispatch(fetchPendingNotification());
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Reject the station function
   */
  const rejectStation = () => {
    setModalView({
      content: (
        <RejectStation
          conformSubmit={stationReject}
          closeModal={closeConfirmModal}
          stationDetails={details}
        />
      ),
      status: true,
      notification: false,
    });
  };

  /**
   * Approved the station function
   */
  const approvedStatus = () => {
    stationApi
      .statusChangeChargingStation(details.id, {
        submissionStatus: 'approved',
      })
      .then(() => {
        setModalView({
          content: (
            <SuccessNotification
              title={t('stationApprovedSuccessfully')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                closeConfirmModal();
                closeModal();
                dispatch(
                  fetchAllUnapprovedStation({
                    page,
                    limit,
                    submissionStatus: 'pending',
                  }),
                );
                dispatch(fetchPendingNotification());
              }}
            />
          ),
          status: true,
          notification: true,
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };
  return (
    <div className="view-charging-station-warp">
      <ModalBox
        title=""
        content={modalView.content}
        closeModal={closeConfirmModal}
        open={modalView.status}
        header={false}
        rightCorner=""
        notification={modalView.notification}
      />
      <div className="facility-details">
        <div className="form-field">
          <CustomInput
            name="name"
            inputLabel={t('stationName')}
            type="text"
            readOnly
            placeholder={t('enterFacilityName')}
            value={details.name}
          />
        </div>
        <div className="form-field">
          <CustomFileInput
            inputLabel={t('landOwnershipDocument')}
            acceptedFileType="image/*, application/pdf"
            placeholder={t('uploadPdfJpeg')}
            name="landOwnerShipDocument"
            existingFiles={details.landOwnerShipDocument}
            onChangeValue={() => {}}
            readOnly
            valueRequired
            selectedFiles={null}
          />
        </div>
        <div className="form-field">
          <CustomFileInput
            inputLabel={t('ebReportsPastSixMonths')}
            acceptedFileType="image/*, application/pdf"
            placeholder={t('uploadPdfJpeg')}
            name="ebReport"
            existingFiles={details.ebReport}
            onChangeValue={() => {}}
            readOnly
            valueRequired
            selectedFiles={null}
          />
        </div>
        <div className="form-field">
          <CustomInput
            name="electricity"
            inputLabel={t('electricityContactType')}
            type="text"
            readOnly
            placeholder={t('enterElectricityContactType')}
            value={details.electricityContractType}
          />
        </div>
        <div className="form-field">
          <CustomInput
            name="time"
            inputLabel={t('operatingHours')}
            readOnly
            type="text"
            placeholder={t('enterOperatingHours')}
            value={`${details.opensAt} - ${details.closesAt}`}
          />
        </div>
        <div className="form-field">
          <CustomInput
            name="address"
            inputLabel={t('address')}
            type="text"
            readOnly
            placeholder={t('enterAddress')}
            value={details.address}
          />
        </div>
        <div className="form-field">
          <CustomInput
            name="lat_lang"
            inputLabel={t('latitudeLongitude')}
            type="text"
            readOnly
            placeholder={t('markLatitudeLongitude')}
            value={details.latLong}
          />
        </div>
        <div className="form-field">
          <CustomMediaUpload
            inputLabel={t('uploadMedia')}
            acceptedFileType="image/* video/*"
            placeholder={t('uploadMediaFormats')}
            name="medias"
            existingFiles={details.medias}
            onChangeValue={() => {}}
            readOnly
            valueRequired
            selectedFiles={null}
          />
        </div>
        {details.amenities.length > 0 && (
          <div className="form-amenities-icons">
            <div className="title-label">{t('amenities')}</div>
            <div className="icon-list">
              {amenitiesDetails
                .filter((icon) => details.amenities.includes(icon.key))
                .map((icon) => (
                  <div className="icon-item">
                    <div className="icon">{icon.svgIconActive}</div>
                    <div className="title">{icon.name}</div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {details.submission.status === 'pending' && (
          <div className="table-bottom-button-list form-bottom-button">
            <div className="table-button-item">
              <MyButton
                label={t('reject')}
                buttonType="danger-outline"
                onClickFunc={rejectStation}
                svgIcon={<CloseCircleIcon />}
              />
            </div>
            <div className="table-button-item">
              <MyButton
                label={t('approve')}
                buttonType="submit"
                onClickFunc={approvedStatus}
                svgIcon={<CheckCircleIcon />}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ViewChargingStation;
